import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useSWR from 'swr';
import _ from 'lodash';
import he from 'he';

import { ADMIN_ACCESS_TOKEN_KEY, getFetcher } from '../../../../App';
import { DEFAULT_CURRENT_PAGE } from '@components/organisms/Table/Pagination';
import { Body, dialogContentsState } from '@components/templates';
import { PrimaryButton, Sub1Button } from '@components/atoms';
import { ErrorCode, ErrorMessage } from '@variables/error';
import { icons } from '@assets/icons';
import CompanyList from './CompanyList';
import TrensferHead from '../TransferHead';
import { useCatchLeave } from 'utils/hooks';
import { DataProps } from '@models/transfer/common';
import {
  BackButtonStyle,
  RightButtonSection,
  StyledArrow,
  StyledFlexColumnsWrapper,
  StyledSendingWrapper,
  SubmitButtonStyle,
} from './style';
import { allDataState, sendingDataState, tbdAllState, tbdSendingState } from '../atom/sending';
import { userPageCurrentPageCommonTransferState } from '../atom/common';

const Sending = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const [accessToken, setAccessToken] = useState('');
  const [selected, setSelected] = useState(false);
  const [valueState, setValueState] = useState({
    searchKey: 'ID',
    searchKeyword: '',
    searchDateType: '',
    dateRange: { startDate: '', endDate: '' },
  });
  const [disabledSubmit, setDisabledSubmit] = useState(true); // submit버튼 ture:비활성화
  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(!disabledSubmit); // disabledSubmit가 false일때 변경사항 있음

  const [cloneSendingData, setCloneSendingData] = useState<DataProps[] | []>([]);

  const setPageConditionsValue = useSetRecoilState(userPageCurrentPageCommonTransferState);
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [allData, setAllData] = useRecoilState(allDataState);
  const [sendingData, setSendingData] = useRecoilState(sendingDataState); // sending company
  const [clickedAll, setClickedAll] = useRecoilState(tbdSendingState); // all company
  const [clickedSending, setClickedSending] = useRecoilState(tbdAllState);

  const sendingCompanyUrl = `api/transfer/${companyId}/sending`;
  const AllCompanyUrl = `api/transfer/companies?companyId=${companyId}`;

  // 데이터 가져오기
  const allDataGet = useSWR(!!accessToken.length ? [{ url: AllCompanyUrl, accessToken }] : null);
  const sendingDataGet = useSWR(!!accessToken.length ? [{ url: sendingCompanyUrl, accessToken }] : null);
  useEffect(() => {
    if (allDataGet.data && sendingDataGet.data) {
      if (allDataGet.data?.code === '200') {
        let allDataList: DataProps[] = [];
        allDataGet.data?.result.forEach((item: DataProps) =>
          allDataList.push({
            id: item.id,
            licenseActivate: item.licenseActivate,
            name: he.decode(item.name),
          }),
        );
        setAllData(allDataList);
      }

      if (sendingDataGet.data?.code === '200') {
        let sendingDataList: DataProps[] = [];
        sendingDataGet.data?.result.forEach((item: DataProps) =>
          sendingDataList.push({
            id: item.id,
            licenseActivate: item.licenseActivate,
            name: he.decode(item.name),
          }),
        );
        setSendingData(sendingDataList);
        setCloneSendingData(sendingDataList);
      }
    }
  }, [allDataGet.data, sendingDataGet.data]);

  useEffect(() => {
    // 토큰 가져오기
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY) || undefined;
    if (newToken && accessToken !== newToken) {
      setAccessToken(newToken);
    }

    // 페이지 확인
    setSelected(location.pathname.includes('receiving'));
  }, []);

  useEffect(() => {
    if (valueState?.searchKeyword)
      setPageConditionsValue((prev) => ({
        ...prev,
        currentPage: DEFAULT_CURRENT_PAGE,
      }));
  }, [valueState]);

  // apply버튼 disabled 토글
  const applyDisabledFC = () => {
    const cloneSending = [...sendingData];
    cloneSending.sort((a, b) => a.id - b.id);
    const isEqual = _.isEqual(cloneSending, cloneSendingData);
    return isEqual || false;
  };

  if (allDataGet.error || sendingDataGet.error) {
    return <div>failed to load</div>;
  }

  if (!allDataGet.data || !sendingDataGet.data) {
    return <div>loading...</div>;
  }

  if (!allDataGet.data.result || !sendingDataGet.data.result) {
    if (allDataGet.data?.code === 'UNAUTHORIZED' || sendingDataGet.data?.code === 'UNAUTHORIZED')
      return <Navigate to={'/'} replace={true} />;
  }

  // 화살표 클릭
  const onClickRight = () => {
    if (clickedAll.length === 0) return;
    setSendingData((prev) => {
      const newList = [...prev, ...clickedAll];
      const sortList = newList.sort((a, b) => Number(a.id) - Number(b.id));
      return sortList;
    });

    setAllData((prev) => {
      const prevA = prev.filter((data) => {
        return clickedAll.findIndex((data2) => data.id === data2.id) === -1;
      });
      return prevA;
    });

    setClickedAll([]);
  };

  const onClickLeft = () => {
    if (clickedSending.length === 0) return;

    setDialogContents({
      type: 'plain',
      body: 'Transfer to the hospital is no longer available if you remove it from the list. Previously transferred patients are retained.',
      footer: (
        <>
          <Sub1Button
            onClick={() => {
              setDialogContents({ type: 'hidden' });
              setClickedSending([]);
            }}
          >
            Cancel
          </Sub1Button>
          <PrimaryButton
            onClick={() => {
              setDialogContents({ type: 'hidden' });

              setAllData((prev) => {
                const newList = [...prev, ...clickedSending];
                const sortList = newList.sort((a, b) => Number(a.id) - Number(b.id));
                return sortList;
              });

              setSendingData((prev) => {
                const prevA = prev.filter((data) => {
                  return clickedSending.findIndex((data2) => data.id === data2.id) === -1;
                });
                return prevA;
              });
              setClickedSending([]);
            }}
          >
            Remove
          </PrimaryButton>
        </>
      ),
    });
  };

  const updateAccount = async (sendingIdList: number[]) => {
    await getFetcher({
      method: 'POST',
      accessToken,
      body: sendingIdList,
    })(sendingCompanyUrl)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'success',
            body: 'It has been reflected.',
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setDisabledSubmit(true);
        } else {
          let header = '';
          let body = '';
          if (ErrorCode[r.code]) {
            header = `${ErrorCode[r.code]}`;
            body = `${ErrorMessage[ErrorCode[r.code]]}`;
          } else {
            header = r.code;
          }

          setDialogContents({
            type: 'error',
            header: `${header}`,
            body: <>{body}</>,
            footer: (
              <PrimaryButton
                onClick={() => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                ok
              </PrimaryButton>
            ),
          });
        }
      })
      .catch((e) => {
        openDialog('Unexpected Error on Update Account', 'Retry', updateAccount);
      });
  };

  const openDialog = (body: string, buttonLabel: string, buttonEvent: any) => {
    setDialogContents({
      type: 'plain',
      body,
      footer: (
        <>
          <Sub1Button onClick={() => setDialogContents({ type: 'hidden' })}>Cancel</Sub1Button>
          <PrimaryButton onClick={buttonEvent}>{buttonLabel}</PrimaryButton>
        </>
      ),
    });
  };

  return (
    <>
      <Body data-page-type={'table-only'}>
        <StyledFlexColumnsWrapper>
          <TrensferHead />

          <StyledSendingWrapper>
            <CompanyList
              type={'All'}
              url={AllCompanyUrl}
              accessToken={accessToken}
              dataList={allData}
              cilckedList={clickedAll}
              setCilckedList={setClickedAll}
            />

            <StyledArrow>
              <img
                className="arrowRight"
                src={clickedAll.length > 0 ? icons.arrowRight : icons.arrowRightDisabled}
                alt={`arrowRight`}
                onClick={onClickRight}
              />
              <img
                className="arrowLeft"
                src={clickedSending.length > 0 ? icons.arrowLeft : icons.arrowLeftDisabled}
                alt={`arrowLeft`}
                onClick={onClickLeft}
              />
            </StyledArrow>

            <CompanyList
              type={'Sending'}
              url={sendingCompanyUrl}
              accessToken={accessToken}
              dataList={sendingData}
              cilckedList={clickedSending}
              setCilckedList={setClickedSending}
            />
          </StyledSendingWrapper>

          <RightButtonSection>
            <Sub1Button onClick={() => navigate('/transfer')} styles={BackButtonStyle}>
              Back
            </Sub1Button>
            <PrimaryButton
              styles={SubmitButtonStyle}
              disabled={applyDisabledFC()}
              onClick={() => {
                const sendingIdList = sendingData.map((company) => company.id);
                updateAccount(sendingIdList);
              }}
            >
              Apply
            </PrimaryButton>
          </RightButtonSection>
        </StyledFlexColumnsWrapper>
      </Body>
    </>
  );
};

export default Sending;
